import React, { useState } from 'react'
import { ArrowLeft, ArrowRight, Check, CheckCircle, Mail, X, Send, RefreshCw, Loader2 } from 'lucide-react'
import { EnrollmentApi } from '@/features/enrollment'
import { handleValidationErrors } from '@/shared/utils'
import ValidationErrorDisplay from './ValidationErrorDisplay'
import { FMOService } from '@/features/fmo/services/fmo.service'

type Props = {
  enrollmentData: any
  updateEnrollmentData: (field: string, value: any) => void
  handleNavigate: (targetIndex: number) => Promise<void>
  handleSimpleNavigate?: (targetIndex: number) => void
  handleNavigateWithCompletion?: (targetIndex: number, steps?: any[]) => Promise<void>
  sessionId: string | null
  isNavigating?: boolean
  isCompleting?: boolean
  currentStepIndex?: number
}

export default function Step9Signature({ enrollmentData, updateEnrollmentData, handleNavigate, handleSimpleNavigate, handleNavigateWithCompletion, sessionId, isNavigating, isCompleting, currentStepIndex }: Props) {
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);

  const handleBack = () => {
    // Calculate previous step dynamically
    const prevStepIndex = (currentStepIndex ?? 8) - 1
    
    if (handleSimpleNavigate) {
      handleSimpleNavigate(prevStepIndex)
    } else if (handleNavigate) {
      handleNavigate(prevStepIndex)
    }
  }

  // Get validation errors for Step 9
  const getValidationErrors = (): string[] => {
    const errors: string[] = []
    
    if (enrollmentData.clientSigned !== true && enrollmentData.clientSigned !== 'not_received') {
      errors.push('Client signature status must be marked as "Signed" or "Not Received Yet" to continue')
    }
    
    return errors
  }
  const [isProcessing, setIsProcessing] = useState(false);
  const [notReceivedConfirmed, setNotReceivedConfirmed] = useState(false);
  const [isMarkingSigned, setIsMarkingSigned] = useState(false);
  
  const handleMarkAsSigned = async () => {
    if (!sessionId) {
      alert('Session ID is missing. Please start the process again.');
      return;
    }

    setIsMarkingSigned(true);
    try {
      // Mark session as signed with additional details
      await EnrollmentApi.markAsSigned(sessionId);
      // Update local state
      updateEnrollmentData('clientSigned', true);
      
      // Show success message
      alert('Client signature confirmed! The system has been updated.');
    } catch (error) {
      console.error('Error marking as signed:', error);
      alert('Failed to mark as signed. Please try again.');
    } finally {
      setIsMarkingSigned(false);
    }
  };

  const handleSendFollowUpEmail = async () => {
    if (!sessionId || !enrollmentData.injuredPartyEmail) {
      alert('Session ID or client email is missing. Please check the enrollment data.');
      return;
    }

    setIsProcessing(true);
    setSendingEmail(true);
    try {
      await EnrollmentApi.sendFollowUpEmail(sessionId, enrollmentData.injuredPartyEmail);
      setEmailSent(true);
      alert('Follow-up email sent successfully! The client will receive instructions about the document signing process.');
    } catch (error) {
      console.error('Error sending follow-up email:', error);
      alert('Failed to send follow-up email. Please try again.');
    } finally {
      setSendingEmail(false);
      setIsProcessing(false);
    }
  };

  const handleNotReceivedYet = () => {
    if (window.confirm('Are you sure you want to mark this as "Not Received Yet"? This will allow you to continue with the enrollment process, but you will need to follow up with the client later to get their signature.')) {
      setNotReceivedConfirmed(true);
      updateEnrollmentData('clientSigned', 'not_received');
    }
  };

  const handleSubmitToFormstack = async () => {
    // Get state-based intake form URL
    let formstackUrl = 'https://game-tort-enroll-process.eliteconnectmarketing.com' // Default fallback
    
    try {
      // Extract victim state from enrollment data
      const victimState = enrollmentData.injuredPartyResidentState || 
                         enrollmentData.injuredPartyState || 
                         enrollmentData.intakeState ||
                         enrollmentData.state
      
      const campaignId = enrollmentData.campaignId || enrollmentData.campaign?.id
      const organizationId = enrollmentData.organizationId
      
      // If we have state and campaign, try to get state-based intake form
      if (victimState && campaignId) {
        try {
          const intakeForm = await FMOService.getIntakeFormByState(
            victimState,
            campaignId,
            organizationId
          )
          if (intakeForm?.url) {
            formstackUrl = intakeForm.url
            console.log('✅ Using state-based intake form:', {
              state: victimState,
              url: formstackUrl,
              vendorName: intakeForm.vendorName
            })
          }
        } catch (error) {
          console.warn('⚠️ Could not get state-based intake form, using default:', error)
          // Continue with default URL
        }
      }
    } catch (error) {
      console.warn('⚠️ Error getting state-based intake form, using default:', error)
    }
    
    const formData = new FormData()

    const appendField = (key: string, value: any) => {
      if (value !== null && value !== undefined && value !== '') {
        formData.append(key, value.toString())
      }
    }

    // Auto-populated fields
    appendField('LIST_ID', enrollmentData.listId)
    appendField('AGENT_NAME', enrollmentData.agentName)
    appendField('DATE', new Date().toLocaleDateString('en-US'))
    appendField('TIME', new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }))

    // Injured Party Details
    appendField('RELATION_TO_VICTIM', enrollmentData.relationToVictim)
    appendField('IS_MINOR', enrollmentData.isMinor ? 'Yes' : 'No')
    appendField('HAS_LEGAL_AUTHORIZATION', enrollmentData.hasLegalAuthorization ? 'Yes' : 'No')
    appendField('IS_CLAIMANT_DECEASED', enrollmentData.isClaimantDeceased ? 'Yes' : 'No')
    appendField('INJURED_PARTY_FIRST_NAME', enrollmentData.injuredPartyFirstName)
    appendField('INJURED_PARTY_MIDDLE_NAME', enrollmentData.injuredPartyMiddleName)
    appendField('INJURED_PARTY_LAST_NAME', enrollmentData.injuredPartyLastName)
    appendField('INJURED_PARTY_SUFFIX', enrollmentData.injuredPartySuffix)
    appendField('GENDER', enrollmentData.gender)
    appendField('INJURED_PARTY_EMAIL', enrollmentData.injuredPartyEmail)
    appendField('INJURED_PARTY_ADDRESS_LINE1', enrollmentData.injuredPartyAddressLine1)
    appendField('INJURED_PARTY_ADDRESS_LINE2', enrollmentData.injuredPartyAddressLine2)
    appendField('INJURED_PARTY_CITY', enrollmentData.injuredPartyCity)
    appendField('INJURED_PARTY_STATE', enrollmentData.injuredPartyState)
    appendField('INJURED_PARTY_ZIP_CODE', enrollmentData.injuredPartyZipCode)
    appendField('PHONE_NUMBER', enrollmentData.phoneNumber)
    appendField('SECONDARY_PHONE_NUMBER', enrollmentData.secondaryPhoneNumber)
    appendField('SSN', enrollmentData.ssn)
    appendField('HAS_ATTORNEY_REPRESENTATION', enrollmentData.hasAttorneyRepresentation ? 'Yes' : 'No')

    // Gaming & Education History
    appendField('IS_GOING_TO_SCHOOL', enrollmentData.isGoingToSchool ? 'Yes' : 'No')
    appendField('HIGHEST_EDUCATION_LEVEL', enrollmentData.highestEducationLevel)
    appendField('GAMING_PLATFORMS_USED', (enrollmentData.gamingPlatformsUsed || []).join(', '))
    ;(enrollmentData.gamerTags || []).forEach((gt: any) => appendField(`GAMER_TAG_${String(gt.platform || '').toUpperCase()}`, gt.tag))
    appendField('GAMES_PLAYED', (enrollmentData.gamesPlayed || []).join(', '))
    ;(enrollmentData.gameHandles || []).forEach((gh: any) => appendField(`GAME_HANDLE_${String(gh.game || '').toUpperCase().replace(/[^A-Z0-9]/g, '_')}`, gh.handle))
    appendField('FIRST_GAME_PLAYED', enrollmentData.firstGamePlayed)
    appendField('MINECRAFT_FIRST_PLAY_YEAR', enrollmentData.minecraftFirstPlayYear)
    appendField('MINECRAFT_HOURS_PER_WEEK', enrollmentData.minecraftHoursPerWeek)
    appendField('DETAILED_GAME_PLAY_INFO', enrollmentData.detailedGamePlayInfo)
    appendField('HAS_CLOUD_GAMING_SUBSCRIPTION', enrollmentData.hasCloudGamingSubscription ? 'Yes' : 'No')
    appendField('MONTHLY_SUBSCRIPTION_COUNT', enrollmentData.monthlySubscriptionCount)
    appendField('USES_VR_HEADSETS', enrollmentData.usesVRHeadsets ? 'Yes' : 'No')
    appendField('HAS_GAME_RECEIPTS', enrollmentData.hasGameReceipts ? 'Yes' : 'No')
    appendField('MONTHLY_GAMING_SPEND', enrollmentData.monthlyGamingSpend)
    appendField('HAS_PROOF_OF_GAMING', enrollmentData.hasProofOfGaming ? 'Yes' : 'No')

    // Health & Legal Background
    appendField('TRIED_TO_STOP_GAMING', enrollmentData.triedToStopGaming ? 'Yes' : 'No')
    appendField('WATCHED_GAMING_INFLUENCERS', enrollmentData.watchedGamingInfluencers ? 'Yes' : 'No')
    appendField('PAID_FOR_IN_GAME_ITEMS', enrollmentData.paidForInGameItems ? 'Yes' : 'No')
    appendField('SOLD_IN_GAME_CONTENT', enrollmentData.soldInGameContent ? 'Yes' : 'No')
    appendField('RECEIVED_ENERGY_DRINK_REWARD', enrollmentData.receivedEnergyDrinkReward ? 'Yes' : 'No')
    appendField('INJURIES_AILMENTS', (enrollmentData.injuriesAilments || []).join(', '))
    appendField('LIFE_AFFECTED_WAYS', (enrollmentData.lifeAffectedWays || []).join(', '))
    appendField('MEDICAL_TREATMENTS_RECEIVED', (enrollmentData.medicalTreatmentsReceived || []).join(', '))
    appendField('DIAGNOSED_GAMING_INJURIES', enrollmentData.diagnosedGamingInjuries ? 'Yes' : 'No')
    appendField('MEDICATION_FOR_GAMING_INJURIES', enrollmentData.medicationForGamingInjuries ? 'Yes' : 'No')
    appendField('WILLING_TO_SPEAK_TO_DOCTOR', enrollmentData.willingToSpeakToDoctor ? 'Yes' : 'No')
    appendField('MEDICAL_PROVIDER_NAME', enrollmentData.medicalProviderName)
    appendField('MEDICAL_PROVIDER_ADDRESS', enrollmentData.medicalProviderAddress)
    appendField('OTHER_MEDICAL_CONDITIONS', enrollmentData.otherMedicalConditions)
    appendField('FILED_LAWSUIT', enrollmentData.filedLawsuit ? 'Yes' : 'No')
    appendField('CONVICTED_MISDEMEANOR_FELONY', enrollmentData.convictedMisdemeanorFelony ? 'Yes' : 'No')
    appendField('RECEIVES_SSDI', enrollmentData.receivesSSDI ? 'Yes' : 'No')
    appendField('RECEIVES_SSI', enrollmentData.receivesSSI ? 'Yes' : 'No')

    // Emergency Contact & Notes
    appendField('EMERGENCY_CONTACT_NAME', enrollmentData.emergencyContactName)
    appendField('EMERGENCY_CONTACT_PHONE', enrollmentData.emergencyContactPhone)
    appendField('EMERGENCY_CONTACT_RELATIONSHIP', enrollmentData.emergencyContactRelationship)
    appendField('NOTES', enrollmentData.notes)

    // NOTE: Real submission may require exact Formstack field names/IDs and CORS setup.
    // This preserves your sample and advances the wizard.
    // try {
    //   const response = await fetch(formstackUrl, { method: 'POST', body: formData })
    //   if (!response.ok) throw new Error(await response.text())
    // } catch (e) {
    //   console.error('Formstack submission failed:', e)
    // }

    await handleNavigate(8)
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg lg:text-xl font-semibold text-accent-900">Client Signature Verification</h3>
            <p className="text-sm text-accent-600">Confirm client has signed the retainer agreement</p>
          </div>
        </div>

        <div className="text-center mb-6">
          <p className="text-base lg:text-lg font-medium text-accent-900 mb-6">
            Has the client signed the legal retainer agreement?
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              className={`px-6 lg:px-8 py-3 lg:py-4 rounded-lg font-semibold text-base lg:text-lg transition-colors flex items-center justify-center ${
                enrollmentData.clientSigned === true
                  ? 'bg-green-600 text-white'
                  : 'bg-green-100 hover:bg-green-200 text-green-700 border-2 border-green-300'
              }`}
              onClick={handleMarkAsSigned}
              disabled={isMarkingSigned}
            >
              {isMarkingSigned ? (
                <>
                  <Loader2 className="w-4 lg:w-5 h-4 lg:h-5 inline mr-2 animate-spin" />
                  Marking as Signed...
                </>
              ) : (
                <>
                  <Check className="w-4 lg:w-5 h-4 lg:h-5 inline mr-2" />
                  Yes, Signed
                </>
              )}
            </button>
            <button
              className={`px-6 lg:px-8 py-3 lg:py-4 rounded-lg font-semibold text-base lg:text-lg transition-colors ${
                enrollmentData.clientSigned === false
                  ? 'bg-red-600 text-white'
                  : 'bg-red-100 hover:bg-red-200 text-red-700 border-2 border-red-300'
              }`}
              onClick={() => updateEnrollmentData('clientSigned', false)}
            >
              <X className="w-4 lg:w-5 h-4 lg:h-5 inline mr-2" />
              Not Yet
            </button>
            <button
              className={`px-6 lg:px-8 py-3 lg:py-4 rounded-lg font-semibold text-base lg:text-lg transition-colors ${
                enrollmentData.clientSigned === 'not_received' || notReceivedConfirmed
                  ? 'bg-orange-600 text-white'
                  : 'bg-orange-100 hover:bg-orange-200 text-orange-700 border-2 border-orange-300'
              }`}
              onClick={handleNotReceivedYet}
            >
              <RefreshCw className="w-4 lg:w-5 h-4 lg:h-5 inline mr-2" />
              Not Received Yet
            </button>
          </div>
        </div>

        {/* Signature Status Indicator */}
        {enrollmentData.clientSigned && (
          <div className={`border rounded-lg p-4 ${
            enrollmentData.clientSigned === true 
              ? 'bg-green-50 border-green-200' 
              : enrollmentData.clientSigned === 'not_received'
              ? 'bg-orange-50 border-orange-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center space-x-2">
              {enrollmentData.clientSigned === true ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : enrollmentData.clientSigned === 'not_received' ? (
                <RefreshCw className="w-5 h-5 text-orange-600" />
              ) : (
                <X className="w-5 h-5 text-red-600" />
              )}
              <span className={`font-semibold ${
                enrollmentData.clientSigned === true 
                  ? 'text-green-800' 
                  : enrollmentData.clientSigned === 'not_received'
                  ? 'text-orange-800'
                  : 'text-red-800'
              }`}>
                {enrollmentData.clientSigned === true 
                  ? '✅ Signature Confirmed' 
                  : enrollmentData.clientSigned === 'not_received'
                  ? '⏳ Not Received Yet - Follow-up Required'
                  : '❌ Not Signed Yet'
                }
              </span>
            </div>
            {enrollmentData.clientSigned === 'not_received' && (
              <p className="text-sm text-orange-700 mt-2">
                You can continue with the enrollment process, but make sure to follow up with the client to get their signature later.
              </p>
            )}
          </div>
        )}

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Mail className="w-5 h-5 text-orange-600 mt-1 flex-shrink-0" />
            <div className="text-sm text-orange-800">
              <p className="font-semibold mb-2">Important Instructions:</p>
              <ul className="space-y-1">
                <li>• Ensure client checks their email for the retainer agreement</li>
                <li>• Client must complete the agreement to proceed with their case</li>
                <li>• Click "Yes, Signed" only after confirming client has signed the document</li>
                <li>• Use "Not Received Yet" if client hasn't signed yet but you want to continue enrollment</li>
                <li>• You can always follow up with the client later to get their signature</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Send Follow-up Email Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Send className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-semibold text-blue-900">Send Follow-up Email</p>
                  <p className="text-sm text-blue-700">Send document signing instructions to the client</p>
                </div>
                <button
                  onClick={handleSendFollowUpEmail}
                  disabled={sendingEmail || emailSent || !enrollmentData.injuredPartyEmail}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    emailSent
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : sendingEmail
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {emailSent ? (
                    <>
                      <Check className="w-4 h-4 inline mr-1" />
                      Sent
                    </>
                  ) : sendingEmail ? (
                    <>
                      <RefreshCw className="w-4 h-4 inline mr-1 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 inline mr-1" />
                      Send Instructions
                    </>
                  )}
                </button>
              </div>
              
              {!enrollmentData.injuredPartyEmail && (
                <p className="text-sm text-red-600">
                  ⚠️ Client email address is required to send follow-up instructions
                </p>
              )}
              
              {emailSent && (
                <div className="bg-green-100 border border-green-300 rounded-lg p-3 mt-3">
                  <p className="text-sm text-green-800">
                    ✅ Follow-up email sent successfully! The client will receive step-by-step document signing instructions.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      <ValidationErrorDisplay 
        errors={getValidationErrors()} 
        title="Cannot continue to Step 10 - Please resolve:"
      />

      <div className="flex justify-between">
        <button
          className="btn-outline flex items-center space-x-2 min-w-[100px] justify-center"
          onClick={handleBack}
          disabled={isNavigating}
        >
          <>
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </>
        </button>

        <button
          className="btn-primary flex items-center space-x-2 min-w-[120px] justify-center"
          onClick={async () => {
            setIsContinuing(true);
            const nextStepIndex = (currentStepIndex ?? 8) + 1
            
            try {
              // Save Step 9 data before navigating
              if (sessionId) {
                try {
                  // ✅ Only save Step 9 specific fields, not all enrollment data
                  await EnrollmentApi.saveStepAuto({
                    sessionId,
                    stepId: 'step9',
                    stepIndex: 8, // Step 9 is index 8
                    stepData: {
                      clientSigned: enrollmentData.clientSigned,
                      signedAt: enrollmentData.clientSigned === true ? new Date().toISOString() : null,
                      signature: enrollmentData.signature,
                      signatureDate: enrollmentData.signatureDate,
                      signatureConsent: enrollmentData.signatureConsent
                      // ❌ Removed ...enrollmentData to prevent saving all previous steps
                    }
                  });
                } catch (saveError) {
                  console.error('❌ Failed to save Step 9 data:', saveError);
                  // Continue with navigation even if save fails
                }
              }
              
              // Use handleNavigateWithCompletion to properly save step data and navigate
              if (handleNavigateWithCompletion) {
                await handleNavigateWithCompletion(nextStepIndex)
              } else {
                await handleNavigate(nextStepIndex)
              }
            } finally {
              setIsContinuing(false);
            }
          }}
          disabled={(enrollmentData.clientSigned !== true && enrollmentData.clientSigned !== 'not_received') || isNavigating || isProcessing || isContinuing}
          title={enrollmentData.clientSigned !== true && enrollmentData.clientSigned !== 'not_received' ? 'Mark as Signed or Not Received Yet to enable' : undefined}
        >
          {isNavigating || isProcessing || isContinuing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  )
}

